/**
 * Leviathan Web - Customer Contracts Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Customer-specific pricing contracts with volume commitments
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GeometricPanel } from '@components/ui/GeometricPanel';
import { API_URL } from '@/config/api';

interface Contract {
  id: string;
  contractNumber: string;
  status: string;
  startDate: string;
  endDate: string;
  volumeCommitment: number | null;
  notes: string | null;
  customer: {
    id: string;
    customerNumber: string;
    name: string;
  };
  _count: {
    prices: number;
  };
}

interface Stats {
  total: number;
  active: number;
  expired: number;
  expiringSoon: number;
}

const CONTRACT_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'bg-gray-500/20 text-gray-400 border-gray-500' },
  ACTIVE: { label: 'Active', color: 'bg-success-green/20 text-success-green border-success-green' },
  EXPIRED: { label: 'Expired', color: 'bg-error-red/20 text-error-red border-error-red' },
  CANCELLED: { label: 'Cancelled', color: 'bg-gray-500/20 text-gray-400 border-gray-500' },
  PENDING_RENEWAL: {
    label: 'Pending Renewal',
    color: 'bg-warning-amber/20 text-warning-amber border-warning-amber',
  },
};

interface StatCardProps {
  label: string;
  value: number | string;
  status?: 'operational' | 'warning' | 'critical';
}

function StatCard({ label, value, status }: StatCardProps) {
  const statusColor =
    status === 'operational'
      ? 'bg-success-green'
      : status === 'warning'
        ? 'bg-warning-amber'
        : status === 'critical'
          ? 'bg-error-red'
          : 'bg-steel';

  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
      <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
      <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
      <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />

      <div className="bg-panel-dark border border-panel-border p-4 clip-path-octagon relative">
        {status && (
          <div className="absolute top-3 right-3">
            <div
              className={`w-2 h-2 ${statusColor} rounded-full animate-pulse`}
              style={{ boxShadow: '0 0 8px currentColor' }}
            />
          </div>
        )}
        <div className="text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
          {label}
        </div>
        <div
          className="text-3xl font-mono font-bold text-amber"
          style={{
            textShadow: '0 0 10px rgba(255, 149, 0, 0.6)',
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
}

const ContractsPage = () => {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [filter, search]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchContracts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (search) params.append('search', search);

      const response = await fetch(`${API_URL}/api/contracts?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationStatus = (endDate: string, status: string) => {
    if (status !== 'ACTIVE') return null;
    const days = getDaysRemaining(endDate);
    if (days < 0) return 'expired';
    if (days <= 30) return 'critical';
    if (days <= 90) return 'warning';
    return 'ok';
  };

  return (
    <div className="space-y-6 relative">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                Customer Contracts
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                Volume Commitments & Special Pricing Agreements
              </p>
            </div>
            <Link
              to="/pricing/contracts/new"
              className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-6 transition-all uppercase tracking-wider relative group"
              style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
            >
              <span className="relative z-10">+ New Contract</span>
              <div
                className="absolute inset-0 bg-gradient-to-r from-amber to-steel opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ mixBlendMode: 'overlay' }}
              />
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Active" value={stats.active} status="operational" />
          <StatCard
            label="Expiring Soon"
            value={stats.expiringSoon}
            status={stats.expiringSoon > 0 ? 'warning' : 'operational'}
          />
          <StatCard
            label="Expired"
            value={stats.expired}
            status={stats.expired > 0 ? 'critical' : undefined}
          />
        </div>
      )}

      {/* Filters */}
      <GeometricPanel>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Search
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Contract #, customer..."
              className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm transition-all placeholder:text-text-tertiary"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
              Status
            </label>
            <div className="flex flex-wrap gap-2">
              {['all', 'ACTIVE', 'DRAFT', 'EXPIRED'].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-2 font-mono text-xs uppercase tracking-wider transition-all ${
                    filter === f
                      ? 'bg-amber text-background'
                      : 'bg-background text-text-secondary hover:text-amber border border-panel-border'
                  }`}
                >
                  {f === 'all' ? 'All' : CONTRACT_STATUS_LABELS[f]?.label || f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setFilter('all');
              }}
              className="w-full bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-2 px-4 transition-all uppercase text-xs tracking-wider border border-panel-border hover:border-steel"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </GeometricPanel>

      {/* Contracts List */}
      {isLoading ? (
        <div className="text-center py-12 text-text-tertiary font-mono animate-pulse">
          &gt; Loading contracts...
        </div>
      ) : contracts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-text-tertiary font-mono text-lg mb-2">
            &gt; No contracts found
          </div>
          <p className="text-text-tertiary font-mono text-sm mb-4">
            {search || filter !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first customer contract to get started'}
          </p>
          <Link
            to="/pricing/contracts/new"
            className="text-amber hover:text-amber/80 font-mono"
          >
            Create your first contract →
          </Link>
        </div>
      ) : (
        <GeometricPanel>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-panel-border">
                  <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Contract #
                  </th>
                  <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Customer
                  </th>
                  <th className="text-center py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Period
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Volume
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Products
                  </th>
                  <th className="text-center py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Expiration
                  </th>
                  <th className="text-right py-3 px-4 text-text-secondary text-xs uppercase tracking-widest font-mono">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => {
                  const expStatus = getExpirationStatus(contract.endDate, contract.status);
                  const daysRemaining = getDaysRemaining(contract.endDate);
                  const statusStyle = CONTRACT_STATUS_LABELS[contract.status] || {
                    label: contract.status,
                    color: 'bg-gray-500/20 text-gray-400 border-gray-500',
                  };

                  return (
                    <tr
                      key={contract.id}
                      onClick={() => navigate(`/pricing/contracts/${contract.id}`)}
                      className="border-b border-panel-border/50 hover:bg-panel-dark/50 cursor-pointer"
                    >
                      <td className="py-3 px-4">
                        <span className="font-mono text-amber">
                          {contract.contractNumber}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-mono text-text-primary">
                          {contract.customer.name}
                        </div>
                        <div className="text-text-tertiary text-xs">
                          #{contract.customer.customerNumber}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`px-2 py-0.5 text-xs font-mono border ${statusStyle.color}`}
                        >
                          {statusStyle.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-text-secondary text-sm">
                        <div>{formatDate(contract.startDate)}</div>
                        <div className="text-text-tertiary">
                          to {formatDate(contract.endDate)}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-text-primary">
                        {contract.volumeCommitment
                          ? contract.volumeCommitment.toLocaleString()
                          : '-'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-amber">
                        {contract._count.prices}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {contract.status === 'ACTIVE' && (
                          <div className="flex items-center justify-center space-x-2">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${
                                expStatus === 'critical'
                                  ? 'bg-error-red'
                                  : expStatus === 'warning'
                                    ? 'bg-warning-amber'
                                    : 'bg-success-green'
                              }`}
                              style={{ boxShadow: '0 0 8px currentColor' }}
                            />
                            <span
                              className={`font-mono text-xs ${
                                expStatus === 'critical'
                                  ? 'text-error-red'
                                  : expStatus === 'warning'
                                    ? 'text-warning-amber'
                                    : 'text-text-secondary'
                              }`}
                            >
                              {daysRemaining <= 0
                                ? 'Expired'
                                : daysRemaining === 1
                                  ? '1 day'
                                  : `${daysRemaining} days`}
                            </span>
                          </div>
                        )}
                      </td>
                      <td
                        className="py-3 px-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() =>
                              navigate(`/pricing/contracts/${contract.id}/edit`)
                            }
                            className="text-text-secondary hover:text-amber font-mono text-sm"
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => navigate(`/pricing/contracts/${contract.id}`)}
                            className="text-steel hover:text-amber font-mono text-sm"
                            title="View"
                          >
                            👁️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GeometricPanel>
      )}
    </div>
  );
};

export default ContractsPage;
